<template>
  <f7-page data-tour-id="samples-list-details-page-root">
    <div>
      <div
        class="custom-table-container tableContainer"
        data-tour-id="samples-list-details-container"
      >
        <div class="summary-header">
          <div class="summary-title">
            <div class="summary-icon"></div>
            <div class="summary-text">
              {{ invoiceCode }}
            </div>
          </div>
          <f7-button
            class="botones-registrar whiteFontColor delivery-download-button"
            fill
            outline
            type="default"
            styling-mode="contained"
            data-tour-id="samples-list-details-download-button"
            @click="downloadInvoiceAtc"
          >
            {{ $t('samplesRegister.downloadDeliveryNoteButton') }}
          </f7-button>
        </div>

        <div
          class="delivery-summary-card"
          data-tour-id="samples-list-details-summary-card"
        >
          <div
            v-if="canEditDeliveryHeader"
            class="delivery-summary-actions"
          >
            <img
              :src="iconPencil"
              class="summary-edit-button"
              data-tour-id="samples-list-details-summary-edit-button"
              :alt="$t('InvoiceDeliveryHeaderEdit')"
              @click="goToEditDeliveryHeader"
            />
          </div>

          <div class="delivery-summary-grid">
            <div class="summary-field">
              <div class="summary-label">
                {{ $t('SamplesList.agency') }}
              </div>
              <div class="summary-value">
                {{ agencyDisplay }}
              </div>
            </div>

            <div class="summary-field">
              <div class="summary-label">
                {{ $t('shipping_date') }}
              </div>
              <div class="summary-value">
                {{ shippingDateDisplay }}
              </div>
            </div>

            <div class="summary-field">
              <div class="summary-label">
                {{ $t('purchase_order') }}
              </div>
              <div class="summary-value">
                {{ orderNumberDisplay }}
              </div>
            </div>

            <div class="summary-field">
              <div class="summary-label">
                {{ $t('SamplesList.totalPrice') }}
              </div>
              <div class="summary-value">
                {{ totalPriceDisplay }}
              </div>
            </div>
          </div>

          <div class="summary-field summary-field--full">
            <div class="summary-label">
              {{ $t('observations') }}
            </div>
            <div class="summary-value">
              {{ observationsDisplay }}
            </div>
          </div>

          <div
            v-if="showColombiaResponsibility"
            class="summary-field summary-field--full colombia-responsibility-display"
          >
            <DxCheckBox
              :value="colombiaResponsibilityAccepted"
              :read-only="true"
              :focus-state-enabled="false"
              :hover-state-enabled="false"
              :active-state-enabled="false"
              :text="
                $t('samplesRegister.colombiaResponsibilityMessage')
              "
            />
          </div>
        </div>

        <samplesListDetails
          :store="invoiceSamples"
          :currency="invoiceCurrency"
        />

        <div
          v-if="canConfirmFromInvoice"
          class="delivery-confirm-action"
        >
          <f7-button
            class="botones-registrar color-green whiteFontColor"
            fill
            outline
            type="default"
            styling-mode="contained"
            @click="goToInvoiceConfirmation"
          >
            {{ $t('ConfirmOrder') }}
          </f7-button>
        </div>
      </div>
    </div>
  </f7-page>
</template>

<script>
import moment from 'moment';
import DxCheckBox from 'devextreme-vue/check-box';
import { mapActions, mapGetters, mapState } from 'vuex';
import samplesListDetails from '@/components/SamplesRegister/SamplesListDetails/index.vue';
import helpers from '@/js/helpers';
import {
  NOTIFY_TIME_LONG,
  SAMPLES_REGISTER_GUIDED_TOUR_AUTO_START_DELAY,
} from '@/js/constants';
import {
  consumePendingSamplesRegisterInvoiceListDetailsTourStart,
  startSamplesRegisterInvoiceListDetailsGuidedTour,
  stopSamplesRegisterGuidedTour,
} from '@/services/samplesRegisterGuidedTour';
import iconPencil from '@/static/icons/ic_pencil.svg';

export default {
  components: {
    DxCheckBox,
    samplesListDetails,
  },
  data() {
    return {
      iconPencil,
      loaded: false,
      guidedTourStartTimeoutId: null,
    };
  },
  computed: {
    ...mapState('contact', ['company']),
    ...mapGetters('contact', ['contactInfo']),
    ...mapState('samplesRegisterModule', [
      'invoice',
      'invoiceSamples',
      'idInvoice',
      'invoiceCode',
      'invoiceState',
      'invoiceDetails',
    ]),
    momentDateFormat() {
      return this.$helpers
        .getDateFormat(this.company)
        .replace('dd', 'DD')
        .replace('yyyy', 'YYYY');
    },
    canEditDeliveryHeader() {
      return this.invoiceState === 'En Transito';
    },
    canConfirmFromInvoice() {
      return this.invoiceState === 'Sin Enviar';
    },
    agencyDisplay() {
      if (this.invoiceDetails?.AgenciaLiteral?.trim()) {
        return this.invoiceDetails.AgenciaLiteral;
      }
      if (this.invoiceDetails?.Agencia === 'SIN AGENCIA') {
        return this.$t('SamplesList.noAgency');
      }
      return this.invoiceDetails?.Agencia || '-';
    },
    shippingDateDisplay() {
      const dateValue =
        this.invoiceDetails?.FechaRecogidaDate ||
        this.invoiceDetails?.FechaRecogida;

      if (!dateValue) return '-';

      const parsedDate = moment(dateValue);
      if (!parsedDate.isValid()) {
        return this.invoiceDetails?.FechaRecogida || '-';
      }

      return parsedDate.format(this.momentDateFormat);
    },
    orderNumberDisplay() {
      const orderNumbers = [
        ...new Set(
          (this.invoiceSamples || [])
            .map((sample) => sample?.Pedido)
            .filter(
              (value) =>
                typeof value === 'string' && value.trim() !== ''
            )
        ),
      ];

      return orderNumbers.length ? orderNumbers.join('; ') : '-';
    },
    totalPriceDisplay() {
      const totalPrice = this.invoiceDetails?.Total;

      if (
        totalPrice === null ||
        totalPrice === undefined ||
        totalPrice === ''
      ) {
        return '-';
      }

      return this.formatAmount(totalPrice, this.invoiceCurrency);
    },
    invoiceCurrency() {
      return (
        helpers.resolveCurrency({
          conversionX3: this.invoiceDetails?.ConversionX3,
          clientCurrency: this.contactInfo?.currency,
          m: this.invoiceDetails?.M,
          moneda: this.invoiceDetails?.Moneda,
          fallback: null,
        }) ||
        helpers.getCurrencyFromInvoiceSamples(
          this.invoiceSamples,
          this.contactInfo?.currency
        )
      );
    },
    observationsDisplay() {
      return this.invoiceDetails?.Observaciones?.trim() || '-';
    },
    isColombiaClient() {
      return String(
        this.invoiceDetails?.CodCliente || this.company || ''
      )
        .trim()
        .toUpperCase()
        .startsWith('CO');
    },
    showColombiaResponsibility() {
      return this.isColombiaClient;
    },
    colombiaResponsibilityAccepted() {
      return this.showColombiaResponsibility;
    },
  },
  async mounted() {
    try {
      if (!this.invoiceDetails && this.invoiceCode) {
        await this.fetchAlbaranes();
        const currentInvoice = (this.invoice || []).find(
          (item) => item.CodAlbaran === this.invoiceCode
        );
        if (currentInvoice) {
          this.setInvoiceDetails(currentInvoice);
        }
      }
    } catch (error) {
      this.$notifyDX(
        {
          message: this.$t(
            this.$helpers.getFilteredErrorMessage(error.message)
          ),
          width: 550,
        },
        'error',
        NOTIFY_TIME_LONG
      );
    } finally {
      this.$f7.preloader.hide();
      this.loaded = true;
      this.scheduleSamplesGuidedTourStart();
    }
  },
  beforeDestroy() {
    this.clearSamplesGuidedTourStartTimeout();
    stopSamplesRegisterGuidedTour(this);
  },
  beforeMount() {
    // this.setCurrentRoute([
    //   {
    //     name: this.$t('SamplesList.orderList'),
    //     disabled: false,
    //     page: 'SamplesList',
    //     active: true,
    //   },
    //   {
    //     name: this.$t('SamplesRegister'),
    //     disabled: true,
    //     page: 'SamplesRegister',
    //     active: false,
    //   },
    // ]);
  },
  methods: {
    async downloadInvoiceAtc() {
      try {
        this.$f7.preloader.show();
        await this.generateInvoicePDF(
          this.invoiceDetails?.idAlbaran || this.idInvoice
        );
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(error.message)
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        this.$f7.preloader.hide();
      }
    },
    clearSamplesGuidedTourStartTimeout() {
      if (this.guidedTourStartTimeoutId) {
        clearTimeout(this.guidedTourStartTimeoutId);
        this.guidedTourStartTimeoutId = null;
      }
    },
    scheduleSamplesGuidedTourStart({ force = false } = {}) {
      if (!this.loaded) return;
      const shouldContinueFromList =
        force ||
        consumePendingSamplesRegisterInvoiceListDetailsTourStart();
      if (!shouldContinueFromList) {
        return;
      }

      this.clearSamplesGuidedTourStartTimeout();
      this.guidedTourStartTimeoutId = setTimeout(() => {
        this.guidedTourStartTimeoutId = null;
        startSamplesRegisterInvoiceListDetailsGuidedTour(this, {
          force: true,
        });
      }, SAMPLES_REGISTER_GUIDED_TOUR_AUTO_START_DELAY);
    },
    formatAmount(amount, currency) {
      if (amount === null || amount === undefined || amount === '') {
        return '';
      }

      const parsedAmount = Number(
        typeof amount === 'string'
          ? amount.replace(',', '.').replace(/[^\d.-]/g, '')
          : amount
      );

      if (Number.isNaN(parsedAmount)) {
        return String(amount);
      }

      return new Intl.NumberFormat('es', {
        style: 'currency',
        currency: helpers.normalizeCurrency(currency) || 'EUR',
        minimumFractionDigits: 2,
      }).format(parsedAmount);
    },
    goToEditDeliveryHeader() {
      this.$f7router.navigate(
        {
          name: 'InvoiceDeliveryHeaderEdit',
        },
        {
          animate: true,
          transition: 'f7-fade',
        }
      );
    },
    async goToInvoiceConfirmation() {
      try {
        this.$f7.preloader.show();
        const currentInvoiceId =
          this.invoiceDetails?.idAlbaran || this.idInvoice;

        if (!currentInvoiceId) {
          throw new Error('missing_invoice_id');
        }

        this.setIdInvoice(currentInvoiceId);
        this.setInvoiceCodeInfoPopUp(
          this.invoiceDetails?.CodAlbaran || this.invoiceCode
        );
        this.setInvoiceState(this.invoiceState);
        this.setInvoiceCode(
          this.invoiceDetails?.CodAlbaran || this.invoiceCode
        );
        this.setInvoiceDetails(this.invoiceDetails);
        await this.fetchInvoiceSamples(currentInvoiceId);

        this.$f7router.navigate(
          {
            name: 'SamplesRegister',
            query: { tab: 1 },
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(error.message)
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        this.$f7.preloader.hide();
      }
    },
    returnToTourInvoiceList() {
      this.$f7router.navigate(
        {
          name: 'SamplesList',
        },
        {
          animate: true,
          transition: 'f7-fade',
        }
      );
    },
    ...mapActions('samplesRegisterModule', [
      'fetchAlbaranes',
      'setInvoiceDetails',
      'setIdInvoice',
      'setInvoiceCodeInfoPopUp',
      'setInvoiceState',
      'setInvoiceCode',
      'fetchInvoiceSamples',
      'generateInvoicePDF',
    ]),
  },
  watch: {
    loaded(value) {
      if (value) {
        this.scheduleSamplesGuidedTourStart();
      }
    },
  },
};
</script>

<style scoped>
.tableContainer {
  border-radius: 8px;
  box-shadow: 0 2px 5px #00346a;
}

.delivery-summary-card {
  position: relative;
  margin: 16px 12px 28px;
  padding: 26px 34px;
  border: 1px solid #dfe5ed;
  border-radius: 14px;
  background: #f5f6f8;
}

.delivery-summary-actions {
  position: absolute;
  top: 22px;
  right: 24px;
}

.summary-edit-button {
  width: 24px;
  height: 24px;
  cursor: pointer;
}

.delivery-download-button {
  margin-left: auto;
  min-height: 40px;
  padding: 0 14px;
  font-size: 16px;
  font-weight: 700;
}

.delivery-summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 28px;
  margin-bottom: 30px;
  padding-right: 28px;
}

.summary-field {
  min-width: 0;
}

.summary-field--full {
  max-width: 100%;
}

.summary-label {
  margin-bottom: 10px;
  color: #00346a;
  font-size: 16px;
  font-weight: 700;
}

.summary-value {
  color: #404040;
  font-size: 15px;
  line-height: 1.45;
  word-break: break-word;
}

.colombia-responsibility-display {
  margin-top: 24px;
}

.colombia-responsibility-display :deep(.dx-checkbox) {
  display: flex;
  align-items: flex-start;
  width: 100%;
  color: #6f7780;
  cursor: default;
}

.colombia-responsibility-display :deep(.dx-checkbox-container) {
  display: flex;
  align-items: flex-start;
  width: 100%;
}

.colombia-responsibility-display :deep(.dx-checkbox-icon) {
  flex: 0 0 auto;
  margin-top: 2px;
  border: 2px solid #838e99;
  border-radius: 4px;
  background: #f3f5f7;
}

.colombia-responsibility-display
  :deep(.dx-checkbox-checked .dx-checkbox-icon) {
  color: #6f7780;
}

.colombia-responsibility-display
  :deep(.dx-checkbox-checked .dx-checkbox-icon::before) {
  color: #6f7780;
}

.colombia-responsibility-display :deep(.dx-checkbox-text) {
  color: #3d3537;
  flex: 1 1 auto;
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  line-height: 1.45;
}

.delivery-confirm-action {
  display: flex;
  justify-content: flex-start;
  margin: 24px 12px 12px;
}

@media (max-width: 1024px) {
  .delivery-summary-grid {
    grid-template-columns: 1fr;
    gap: 20px;
    margin-bottom: 20px;
    padding-right: 0;
  }
}
</style>
